import { useSplitTreatments } from "@splitsoftware/splitio-react";
import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { useEffect, useId, useState } from "react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { ExploreSearchParams } from "@/routes/explore/index";

import { ButtonIcon, CheckmarkIcon } from "./icons";

interface ChristmasFilterProps extends React.ComponentProps<"input"> {
  label?: string;
}

export default function ChristmasFilter({
  className,
  label = "Get festive! ⛄️",
  ...props
}: ChristmasFilterProps) {
  const search = useSearch({ strict: false }) as ExploreSearchParams;
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [isChecked, setIsChecked] = useState(props.defaultChecked);
  const id = useId();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      navigate({
        replace: true,
        search: { ...search, xmas_tag: "Yes" },
        to: pathname,
      });
    } else {
      const { xmas_tag: _, ...newSearch } = search;
      navigate({ replace: true, search: newSearch, to: pathname });
    }

    if (typeof props.onChange === "function") {
      props.onChange(event);
    }
  };

  useEffect(() => {
    setIsChecked(search.xmas_tag === "Yes");
  }, [search.xmas_tag]);

  const { isReady, treatments } = useSplitTreatments({
    names: ["xmas_bash_2023"],
  });

  if (!isReady) {
    return null;
  }
  if (!("xmas_bash_2023" in treatments)) {
    return null;
  }
  if (treatments.xmas_bash_2023.treatment !== "on") {
    return null;
  }

  return (
    <label
      className={cn(
        "relative flex h-7.5 cursor-pointer items-center justify-center overflow-hidden rounded-[15px] shadow-powder-blue transition-shadow",
        isChecked && "shadow-0-0-10",
        className
      )}
      htmlFor={id}
    >
      <Input
        {...props}
        className="hidden"
        id={id}
        name="xmas_tag"
        onChange={handleChange}
        type="checkbox"
        value="Yes"
      />
      <ButtonIcon className="absolute inset-0" />
      <span className="absolute top-2 left-3 z-1 flex size-3.5 items-center justify-center rounded-[2px] bg-space-gray-dark">
        <CheckmarkIcon
          className={cn("transition-opacity", !isChecked && "opacity-0")}
        />
      </span>
      <span className="relative z-1 pl-6 font-semibold text-12 text-rich-black">
        {label}
      </span>
    </label>
  );
}
